import {useLocalStorage} from "@vueuse/core";
import {computed} from 'vue';
import {defineStore} from 'pinia';
import {apiClient} from '@/api-client';
import type {
    BassLayoutSettings,
    BeamSearchSettings,
    ChordInferenceSettings,
    ConversionSettings,
    LyricSettings,
} from '@/generated';
// Direkt aus dem Datenmodul, NICHT über den Barrel: der exportiert auch den
// BassLayoutEditor (SFC) und zöge ihn in jeden Test, der bloß die Settings braucht.
import {
    cloneBassLayout,
    DEFAULT_BASS_LAYOUT,
    INSTRUMENT_TUNINGS,
    instrumentTuning,
} from '@/components/harmonika/instrumentData';

type ConversionMode = 'global' | 'auto' | 'per_measure';

const SETTINGS_STORAGE_KEY = 'griffschrift:settings';
const MODE_STORAGE_KEY = 'griffschrift:mode';
// UI-Präferenz „Bass-Teil weglassen" – bewusst SEPARAT von ConversionSettings (darf nicht in den
// Convert-Request leaken; die Griffschrift-Pipeline entfernt Bass ohnehin unkonditionell). Muster
// wie MODE_STORAGE_KEY: eigener localStorage-Key statt Teil des Settings-Objekts.
const REMOVE_BASS_STORAGE_KEY = 'griffschrift:removeBass';
// UI-Präferenz „Stimmung meiner Harmonika" – ebenfalls kein Teil von ConversionSettings: die
// Engine rechnet in G-C-F-B, Griffschrift ist stimmungsunabhängig. Wirkt nur auf Beschriftung und
// Klang (Simulator, Akkord-Finder).
const INSTRUMENT_TUNING_STORAGE_KEY = 'griffschrift:instrumentTuning';
// UI-Präferenz „Bassknöpfe anzeigen" im Akkord-Finder – reine Anzeige, kein Teil von ConversionSettings.
const CHORD_FINDER_SHOW_BASS_STORAGE_KEY = 'griffschrift:chordFinderShowBass';

const defaultChordInference: ChordInferenceSettings = {
    // Opt-in: der frische Upload erkennt Akkorde NICHT automatisch (kein ungefragtes Einfügen).
    // Nur bei `enabled` schickt der Upload das `chord_inference`-Formfeld mit – das Feld ist
    // serverseitig zugleich das Gate der Upload-Erkennung (siehe useMusicUploadStore).
    enabled: false,
    // Folk-Engine-Regler (Defaults == FolkChordAnalysisConfig, siehe griffschrift.chords.inference).
    min_chord_confidence: 0.52,
    non_chord_tone_tolerance: 0.30,
    max_pitch_classes: 4,
    merge_repeated_symbols: true,
    detect_changes_within_measure: true,
    // Nur Akkorde mit Akkordknopf im Bass-Layout (Harmonika-Tab); Layout reist im Request mit.
    restrict_to_bass_layout: true,
    // null = Tonart analysieren; sonst feste Tonart ("F", "B-", "Dm").
    key_override: null,
    follow_key_changes: true,
    // `overwrite_existing` steht bewusst NICHT hier: es ist keine persistierte Präferenz,
    // sondern eine per-Aktion-Entscheidung (Checkbox im ChordInferencePanel). Requests, die es
    // nicht wollen, pinnen es explizit auf false (stores/settingsPins.ts, Upload-Store).
};

// Beam-Search-Defaults (spiegeln griffschrift.config.settings.BeamSearchSettings). max_druck_zug_ratio
// null = Druck/Zug-Balance-Begrenzung AUS (Default -> keine Verhaltensänderung). Der beamField-Getter
// fällt auf diese Defaults zurück, falls ein persistiertes/serverseitiges Beam-Objekt das Feld nicht kennt.
const defaultBeamSettings: BeamSearchSettings = {
    use_beam_search: true,
    beam_width: 20,
    enable_dubug: false,
    transition_penalty: 1.051,
    balance_druck_zug: true,
    max_druck_zug_ratio: null,
};

const defaultSettings: ConversionSettings = {
    ChordInference: {...defaultChordInference},
    Beam: {...defaultBeamSettings},
    // Physische Belegung der Bass-Seite. Spiegel von griffschrift/mapping/bass_layout.py
    // (Abgleich: tests/test_bass_layout_alignment.py). Reist im Convert-Request mit, weil die
    // Engine daraus die Bass-Zeile ableitet.
    BassLayout: cloneBassLayout(DEFAULT_BASS_LAYOUT),
    // MAX_ALTERNATIVES_DRUCK: 2,
    // MAX_ALTERNATIVES_ZUG: 2,
    // include_druck: true,
    // include_zug: true,
    // include_alternative_druck: true,
    // include_alternative_zug: true,
    // enforce_c_major: false,
    // combine: true,
    // correct_takt: false,
    // add_measurements: false,
    // measure_size: 4,
    // reduce_octave: 0,
    // debug_output: false,
    // selected_parts: [],
} as ConversionSettings;

function loadPersistedSettings(): ConversionSettings {
    const raw = localStorage.getItem(SETTINGS_STORAGE_KEY);
    if (!raw) {
        // getSettings().then(JSON.stringify).then(r => {
        //     localStorage.setItem(SETTINGS_STORAGE_KEY, r);
        // })
        return {...defaultSettings};
    }

    try {
        return {...defaultSettings, ...(JSON.parse(raw) as ConversionSettings)};
    } catch {
        return {...defaultSettings};
    }
}

/**
 * Get settings (Server-Defaults; nur Erststart/Reset – lokale Settings haben Vorrang).
 */
async function getSettings() {
    return apiClient.getSettings();
}


type ConversionSettingsAll = Required<ConversionSettings> | ConversionSettings;
export const useSettingsStore = defineStore('settings', () => {
    let settingsMaxAge = Temporal.Now.instant().subtract({
        hours: 6,
    });
    // Bidirektional persistiert (String-Serializer: bestehende Roh-Werte bleiben lesbar). Ein
    // plain ref las den Key früher nur – die Modus-Wahl ging bei jedem Reload verloren.
    const mode = useLocalStorage<ConversionMode>(MODE_STORAGE_KEY, 'auto');
    // Persistierte UI-Präferenz „Bass-Teil entfernen" (Default AUS = Bass bleibt erhalten). Der
    // Nutzer sieht seine Datei vollständig – auch beim PDF-Import – und entfernt den Bass nur
    // aktiv. Speist die PDF-Import-Checkbox (UploadCard) und den compact-Bass-Toggle im
    // VoiceMergePanel. useLocalStorage persistiert bidirektional (Wahl wird gemerkt).
    const removeBassParts = useLocalStorage<boolean>(REMOVE_BASS_STORAGE_KEY, false);
    // Schlüssel aus INSTRUMENT_TUNINGS; ein unbekannter gespeicherter Wert fällt beim Lesen auf
    // G-C-F-B (`instrumentTuning`).
    const instrumentTuningId = useLocalStorage<string>(INSTRUMENT_TUNING_STORAGE_KEY, INSTRUMENT_TUNINGS[0]!.id);
    const instrumentTuningOffset = computed(() => instrumentTuning(instrumentTuningId.value).offset);
    const chordFinderShowBass = useLocalStorage<boolean>(CHORD_FINDER_SHOW_BASS_STORAGE_KEY, false);
    // Vor dem useLocalStorage-Aufruf prüfen: useLocalStorage schreibt seinen Default selbst in
    // localStorage, daher muss "war schon etwas gespeichert?" davor erfasst werden.
    const hadPersistedSettings = localStorage.getItem(SETTINGS_STORAGE_KEY) !== null;
    // `selected_parts` ist Per-DATEI-Zustand (Convert-Scope; nach jeder Analyse auf alle Parts
    // gesetzt) und wird bewusst NICHT persistiert: eine gespeicherte Part-Auswahl von Datei A
    // wirkte sonst nach einem Reload auf Datei B. Der Serializer strippt das Feld in beide
    // Richtungen; im laufenden Store bleibt es normal nutzbar. Test:
    // settingsSelectedPartsNotPersisted.spec.ts.
    const settingsSerializer = {
        read: (raw: string): ConversionSettingsAll => {
            try {
                const parsed = JSON.parse(raw) as ConversionSettings;
                delete parsed.selected_parts;
                return {...defaultSettings, ...parsed};
            } catch {
                return {...defaultSettings};
            }
        },
        write: (value: ConversionSettingsAll): string => {
            const {selected_parts: _perFile, ...persisted} = value as ConversionSettings;
            return JSON.stringify(persisted);
        },
    };
    const settings = useLocalStorage<ConversionSettingsAll>(SETTINGS_STORAGE_KEY, () => defaultSettings,
        {serializer: settingsSerializer});
    const settingsResetAt = useLocalStorage<Temporal.Instant | null>(SETTINGS_STORAGE_KEY + "lastReset", () => null);


    function updateSettings(partial: Partial<ConversionSettingsAll>) {
        const update = {...(settings.value ?? loadPersistedSettings()), ...partial} as ConversionSettingsAll;
        settings.value = update;
        // Persistenz erfolgt ausschließlich über useLocalStorage; kein Server-Sync mehr.
        // Die Konvertierung erhält die Settings request-isoliert im Convert-Request-Body.
    }


    async function loadSettingsFromServer() {
        const settingsServer = await getSettings()
        if (settingsServer?.settings) {
            settings.value = settingsServer.settings;
            settingsResetAt.value = Temporal.Now.instant();
        }
    }

    const field = <K extends keyof ConversionSettings>(key: K, defaultV?: ConversionSettings[K]) =>
        computed({
            get: () => settings.value?.[key] as ConversionSettings[K] ?? defaultV,
            set: (value: ConversionSettings[K]) => {
                updateSettings({[key]: value} as Partial<ConversionSettings>)
            },
        });

    const lyricField = <K extends keyof LyricSettings>(key: K) =>
        computed({
            get: () => (settings.value ?? defaultSettings).Lyrics?.[key] as LyricSettings[K],
            set: (value: LyricSettings[K]) => {
                const updatedLyrics = {...settings.value.Lyrics, [key]: value};
                settings.value.Lyrics = updatedLyrics;
            },
        });

    /** Zugriff auf einzelne Felder der Akkorderkennungs-Settings (persistiert, nested). */
    const chordInferenceField = <K extends keyof ChordInferenceSettings>(key: K) =>
        computed({
            // Defaults drunterlegen: ein in localStorage persistiertes Objekt aus einer älteren
            // Version kennt neu hinzugekommene Felder nicht (die UI zeigte sonst `undefined`).
            get: () => ({
                ...defaultChordInference,
                ...((settings.value ?? defaultSettings).ChordInference ?? {}),
            })[key],
            set: (value: ChordInferenceSettings[K]) => {
                const current = {...defaultChordInference, ...(settings.value.ChordInference ?? {})};
                settings.value.ChordInference = {...current, [key]: value};
            },
        });

    /** Die aktuellen Akkorderkennungs-Settings als vollständiges Objekt (mit Defaults). */
    const chordInference = computed<ChordInferenceSettings>(() => ({
        ...defaultChordInference,
        ...((settings.value ?? defaultSettings).ChordInference ?? {}),
    }));

    /** Zugriff auf einzelne Felder der Beam-Search-Settings (persistiert, nested). Fällt für fehlende
     *  Felder (altes persistiertes/serverseitiges Beam-Objekt) auf defaultBeamSettings zurück. */
    const beamField = <K extends keyof BeamSearchSettings>(key: K) =>
        computed({
            get: () =>
                ((settings.value ?? defaultSettings).Beam ?? defaultBeamSettings)[key] ??
                defaultBeamSettings[key],
            set: (value: BeamSearchSettings[K]) => {
                const current = settings.value.Beam ?? {...defaultBeamSettings};
                settings.value.Beam = {...current, [key]: value};
            },
        });

    /**
     * UI-Modelle des Druck/Zug-Ausgleichs (nur Auto-Modus): `balanceEnabled` ist der
     * Enable-Schalter – Aktivieren setzt beim ersten Mal das Default-Verhältnis 2.2 (= höchstens
     * 11:5), Deaktivieren behält den Wert (der Gate greift über `balance_druck_zug`).
     * `maxRatioModel` normalisiert leere/NaN-Eingaben des Zahlenfelds (`v-model.number` liefert
     * bei geleertem Feld '' bzw. NaN) auf null (= aus) – ein roher Wert würde persistiert und
     * ließe den Convert-Request (pydantic `float | None`) mit 422 scheitern.
     *
     * Hier statt in SettingsBasic.vue, damit `beamMaxDruckZugRatio.spec.ts` den ECHTEN Code
     * testet statt einer nachgebauten Kopie.
     */
    function beamBalanceModel() {
        const DEFAULT_MAX_RATIO = 2.2;
        const balanceDruckZug = beamField('balance_druck_zug');
        const maxDruckZugRatio = beamField('max_druck_zug_ratio');
        const balanceEnabled = computed({
            get: () => !!balanceDruckZug.value,
            set: (value: boolean) => {
                balanceDruckZug.value = value;
                if (value && (maxDruckZugRatio.value === null || maxDruckZugRatio.value === undefined)) {
                    maxDruckZugRatio.value = DEFAULT_MAX_RATIO;
                }
            },
        });
        const maxRatioModel = computed<number | null>({
            get: () => maxDruckZugRatio.value ?? null,
            set: (value) => {
                const invalid =
                    value === null ||
                    value === undefined ||
                    (value as unknown) === '' ||
                    (typeof value === 'number' && Number.isNaN(value));
                maxDruckZugRatio.value = invalid ? null : value;
            },
        });
        return {balanceEnabled, maxRatioModel};
    }

    /**
     * Die konfigurierte Bass-Seite. Fällt auf das Default-Layout zurück, damit ein vor diesem
     * Feature persistierter localStorage-Stand nicht als leere Bass-Seite erscheint.
     *
     * Geschrieben wird über `setBassLayout` statt über ein schreibbares Computed: Pinia gibt
     * Computeds eines Setup-Stores als **lesende** Referenz heraus, ein `store.bassLayout = …`
     * scheitert schon am Typcheck. Die Granularität ist bewusst das ganze Layout – der Editor
     * fügt Reihen und Knöpfe hinzu und entfernt sie, ein Feld-Setter je Knopf träfe das nicht.
     */
    const bassLayout = computed<BassLayoutSettings>(
        () => (settings.value ?? defaultSettings).BassLayout ?? cloneBassLayout(DEFAULT_BASS_LAYOUT),
    );

    function setBassLayout(value: BassLayoutSettings) {
        updateSettings({BassLayout: value});
    }

    function resetBassLayout() {
        setBassLayout(cloneBassLayout(DEFAULT_BASS_LAYOUT));
    }

    function setSelectedParts(indices: number[]) {
        updateSettings({selected_parts: indices});
    }

    async function resetSettings() {
        // settings.value = {...defaultSettings};
        await loadSettingsFromServer()
        mode.value = 'auto';
        // localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settings.value));
        // localStorage.setItem(MODE_STORAGE_KEY, mode.value);
    }

    // Bootstrap direkt beim ersten Instanziieren des Stores (NICHT onMounted: der Hook feuert nur,
    // wenn der Store erstmals in einem Component-Setup erzeugt wird – aus einem anderen Store heraus
    // liefe er nie). Server nur beim Erststart (leerer localStorage) als Default-Quelle nutzen –
    // vorhandene lokale Settings werden NICHT überschrieben.
    if (!hadPersistedSettings || !settingsResetAt.value || Temporal.Instant.compare(settingsResetAt.value, settingsMaxAge) < 0) {
        void loadSettingsFromServer().catch(() => {
            /* Offline/Serverfehler beim Bootstrap: lokale Defaults reichen. */
        });
    }
    return {

        mode,
        removeBassParts,
        instrumentTuningId,
        instrumentTuningOffset,
        chordFinderShowBass,
        field,
        lyricField,
        chordInferenceField,
        chordInference,
        beamField,
        beamBalanceModel,
        bassLayout,
        setBassLayout,
        resetBassLayout,
        setSelectedParts,
        settings,
        updateSettings,
        resetSettings,
    } as const;
});
